import React, { useState } from 'react';
import { useUsers } from '../../../metodos/userContext';
import { Tutor } from '../../../objetos/objeto_tutor';
import { 
  User, Book, GraduationCap, Calendar, Briefcase, 
  Globe, Mail, Phone, Star, FileText, CreditCard, 
  AlertTriangle, Award, CheckCircle, MapPin, Clock, ExternalLink
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { EditableWidget } from '../../../components/EditableWidget';
import ListaTiposServicios from '../../../views/Utils/listaTiposServicios';
import SelectorTipoServicios from '../../../views/Utils/selectorTipoServicios';
import ListaMaterias from '../../../views/Utils/listaMaterias';
import SelectorMaterias from '../../../views/Utils/selectorMaterias';
import ListaCuentasBancarias from '../../../views/Utils/listaCuentasBancarias';
import CuentasBancariasForm from '../../../views/Utils/selectorCuentaBancaria';
import { formatearFechaCompleta } from '../../../utils/utils';

//cargar skeletos, se ve melo
// Componente esqueleto para carga
const ProfileSkeleton = () => (
  <div className="flex flex-col w-full max-w-4xl mx-auto bg-gray-50 min-h-screen p-4 space-y-4">
    <div className="bg-white shadow-md p-6 rounded-lg">
      <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
        <Skeleton className="w-32 h-32 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    </div>
    
    <div className="w-full">
      <Skeleton className="h-10 w-full mb-4" />
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  </div>
);

// Componente para mostrar el estado del tutor
const TutorStatusBadge = ({ activo, expulsado, estaSancionado }) => {
  if (expulsado) {
    return <Badge className="bg-red-500">Expulsado</Badge>;
  } else if (estaSancionado) {
    return <Badge className="bg-yellow-500">Sancionado</Badge>;
  } else if (activo) {
    return <Badge className="bg-green-500">Activo</Badge>;
  } else {
    return <Badge className="bg-gray-500">Inactivo</Badge>;
  }
};

const PerfilUsuario = () => {
  const { usuario, loading, error } = useUsers();
  const [activeTab, setActiveTab] = useState("perfil");
  // Obtener la última conexión

  // Mostrar esqueleto de carga
  if (loading) {
    return <ProfileSkeleton />;
  }

  // Mostrar error si hubo alguno
  if (error || !usuario) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">No se pudo cargar el perfil</h2>
        <p className="text-gray-600">{error || "Ocurrió un error al cargar los datos del tutor"}</p>
      </div>
    );
  }

    /**
   * Componente para renderizar una tarjeta de solicitud
   * @param {Tutor} usuario - La solicitud a renderizar
   */
  return (
    <div className="flex flex-col w-full max-w-4xl mx-auto bg-gray-50 min-h-screen p-4">
      
      {/* Cabecera con información principal */}
      <div className="bg-white shadow-md p-6 rounded-lg mb-4">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Imagen y verificado*/}

                <div className="relative">
                  <img 
                    src={usuario.fotoPerfil || "/api/placeholder/150/150"} 
                    alt={usuario.nombrecompleto} 
                    className="w-32 h-32 rounded-full object-cover border-4 border-blue-100"
                  />
                  {usuario.tutorVerificado && (
                    <Badge className="absolute -bottom-2 -right-2 bg-blue-500">
                      <CheckCircle className="w-4 h-4 mr-1" /> Verificado
                    </Badge>
                  )}
                </div>
                
                {/*informacion de tutor */}
                <div className="flex-1 text-center md:text-left">
                  <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-800">{usuario.nombrecompleto}</h1>
                    <TutorStatusBadge 
                      activo={usuario.activo}
                      expulsado={usuario.expulsado}
                      estaSancionado={usuario.estaSancionado}
                    />
                    <Badge className="bg-blue-600">{usuario.rol}</Badge>
                  </div>
      
                  <p className="text-gray-600 flex items-center justify-center md:justify-start mt-1">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    {usuario.carrera}
                  </p>
                  <p className="text-gray-600 flex items-center justify-center md:justify-start">
                    <Book className="w-4 h-4 mr-2" />
                    {usuario.universidad}
                  </p>
                  <p className="text-gray-600 flex items-center justify-center md:justify-start">
                    <MapPin className="w-4 h-4 mr-2" />
                    {usuario.ciudadOrigen}, {usuario.paisOrigen}
                  </p>

                  {/* Información de contacto: Número WhatsApp */}
                  <p className="text-gray-600 flex items-center justify-center md:justify-start">
                    <Phone className="w-4 h-4 mr-2" />
                    {usuario.numerowhatsapp}
                  </p>
                  
                  {/* Información de contacto: Correo electrónico */}
                  <p className="text-gray-600 flex items-center justify-center md:justify-start">
                    <Mail className="w-4 h-4 mr-2" />
                    {usuario.correogmail}
                  </p>
                  
                </div>
                
              </div>
      </div>

      {/* Tabs de navegación */}
      <Tabs defaultValue="perfil" className="w-full" onValueChange={setActiveTab} value={activeTab}>
        <TabsList className="grid grid-cols-5 mb-4">
          <TabsTrigger value="perfil">Perfil</TabsTrigger>
          <TabsTrigger value="materias">Materias</TabsTrigger>
          <TabsTrigger value="experiencia">Experiencia</TabsTrigger>
          <TabsTrigger value="servicios">Sanciones</TabsTrigger>
          <TabsTrigger value="pagos">Cuentas Bancarias</TabsTrigger>
        </TabsList>
      
          {/* Perfil */}
          <TabsContent value="perfil" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Información Personal</CardTitle>
                  <CardDescription>
                    Datos profesionales y personales del tutor
                  </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-700 flex items-center">
                          <FileText className="w-4 h-4 mr-2" /> Resumen Profesional
                        </h3>

                        <div className="mt-1 text-gray-600 flex items-center">
                        
                        <a 
                            href={usuario.hojadevida } 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1 rounded-md transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Ver hoja de vida
                        </a>

                      </div>                      
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-700 flex items-center">
                          <GraduationCap className="w-4 h-4 mr-2" /> Formación Académica
                        </h3>
                        <p className="mt-1 text-gray-600">{usuario.formacionAcademica || "No hay información disponible"}</p>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-700 flex items-center">
                          <User className="w-4 h-4 mr-2" /> Identificación
                        </h3>
                        <div className="mt-1 text-gray-600">
                          <p>Tipo: {usuario.tipoDocumento || "No especificado"}</p>
                          <p>Número: {usuario.identificacionTutor || "No especificado"}</p>
                        </div>
                      </div>
                      
                    
                      
                      <div>
                        <h3 className="font-semibold text-gray-700 flex items-center">
                          <Calendar className="w-4 h-4 mr-2" /> Información Adicional
                        </h3>
                        <div className="mt-1 text-gray-600">
                          <p>Creación del perfil: {formatearFechaCompleta(usuario.fechaCreacion)}</p>
                          {usuario.nombreEmpresa && (
                            <p>Empresa: {usuario.nombreEmpresa}</p>
                          )}
                         
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <EditableWidget
                  title={"Tipos de servicios"}
                    displayWidget={
                            <ListaTiposServicios
                              tiposServicios={usuario.tipoServicios}
                            />
                    }
                    editWidget={
                            <SelectorTipoServicios
                              serviciosSeleccionados={usuario.tipoServicios}
                              //formData={formData}
                              //setFormData={setFormData}
                            />
                    }
                    onSave={() =>{
                            //updateTutorLocalAuxiliar('tipoServicios',formData.tipoServicios,'field');
                    }}
                  canEdit={false}
              />
                
                {usuario.anexos && usuario.anexos.urlHojaDeVida && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Documentos Anexos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <a 
                        href={usuario.anexos.urlHojaDeVida} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                      >
                        <FileText className="w-5 h-5 mr-2" />
                        Hoja de Vida (CV)
                      </a>
                    </CardContent>
                  </Card>
                )}
          </TabsContent>
      
          {/* Materias */}
          <TabsContent value="materias" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Materias</CardTitle>
                    <CardDescription>
                      Asignaturas en las que el tutor está especializado
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                     <EditableWidget
                      title={"Materias manejadas"}
                      displayWidget={
                        <ListaMaterias
                          materias={usuario.materias}
                        />
                      }
                      editWidget={
                        <SelectorMaterias 
                        //materias={materias} 
                        //materiasSeleccionadas={materiaSeleccioandas}
                        //setMateriasSeleccionadas={setMateriasSeleccionadas}
                      />
                      }
                      onSave={() =>{
                        //updateTutorLocalAuxiliar('materias',materiaSeleccioandas,'field');
                      }}
                      //isLoading={}
                      canEdit={false}
                      />
                  </CardContent>
                </Card>
          </TabsContent>
      
          {/* Experiencia laboral */}
          <TabsContent value="experiencia" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Experiencia Laboral</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {usuario.experienciaLaboral && usuario.experienciaLaboral.length > 0 ? (
                      <div className="space-y-6">
                        {usuario.experienciaLaboral.map((exp, index) => (
                          <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                            <div className="flex justify-between">
                              <h3 className="font-bold text-gray-800">{exp.nombrEmpresa}</h3>
                              <Badge className={exp.trabajandoActualmente ? "bg-green-500" : "bg-gray-500"}>
                                {exp.trabajandoActualmente ? "Actual" : "Finalizado"}
                              </Badge>
                            </div>
                            <p className="text-gray-600 text-sm flex items-center mt-1">
                              <Briefcase className="w-4 h-4 mr-2" /> {exp.modalidad}
                            </p>
                            <p className="text-gray-600 text-sm flex items-center">
                              <Calendar className="w-4 h-4 mr-2" />
                              {formatearFechaCompleta(exp.fechaIncio)} - {exp.trabajandoActualmente ? "Presente" : formatearFechaCompleta(exp.fechaFinal)}
                            </p>
                            <p className="mt-2">{exp.descripcion}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-6 text-gray-500">
                        No hay experiencia laboral registrada
                      </div>
                    )}
                  </CardContent>
                </Card>
          </TabsContent>
      
          {/* Sanciones */}
          <TabsContent value="servicios" className="space-y-4">
            Trabajando en sistema de sanciones...
          </TabsContent>
      
           {/* Cuentas bancarias */}
          <TabsContent value="pagos" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Información de Pago</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EditableWidget
                      title={"Cuentas Bancarias"}
                      displayWidget={
                        <ListaCuentasBancarias
                          cuentas={usuario.cuentas}
                        />
                      }
                      editWidget={
                        <CuentasBancariasForm
                          cuentas={usuario.cuentas} 
                          //formData={formData} 
                          //setFormData={setFormData} 
                        />
                      }
                      onSave={() =>{
                        //updateTutorLocalAuxiliar('cuentas',formData.cuentas,'field');
                      }}
                      //isLoading={}
                      canEdit={false}
                    />
                  </CardContent>
                </Card>
          </TabsContent>
      </Tabs>

    </div>
  )
}

export default PerfilUsuario
